import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatLegacyAutocomplete } from '@angular/material/legacy-autocomplete';
import { LucideIconInput } from '@lucide/angular';

/** Shared control scale: 's' is 32px, 'm' is 40px. Keep in step with ptl-select and ptl-button. */
export type SgInputSize = 's' | 'm';

@Component({
  selector: 'ptl-sg-input',
  templateUrl: './sg-input.component.html',
  styleUrls: ['./sg-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SgInputComponent),
      multi: true,
    },
  ],
})
export class SgInputComponent implements ControlValueAccessor {
  @Input() type = 'text';
  @Input() placeholder = '';
  @Input() label = '';
  @Input() ariaLabel = '';
  @Input() ariaExpanded: boolean | null = null;
  @Input() ariaControls: string | null = null;
  @Input() description = '';
  @Input() disabled = false;
  /** For a value to copy rather than edit; its text is selected on focus. */
  @Input() readonly = false;
  /** Tints the field red. Driven by the host's own validation, so the input stays presentational. */
  @Input() invalid = false;
  @Input() withIcon = false;
  @Input() icon: LucideIconInput;
  @Input() maxLength: number | null = null;
  @Input() min: number | null = null;
  @Input() max: number | null = null;
  @Input() matAutocomplete: MatLegacyAutocomplete;
  @Input() size: SgInputSize = 'm';
  /** Shows a clear (×) button once the field holds a value. */
  @Input() clearable = false;
  @Input() clearAriaLabel?: string;

  @Output() inputChange = new EventEmitter<string>();
  @Output() cleared = new EventEmitter<void>();
  @Output() focusoutAction = new EventEmitter<void>();
  @Output() focustEnterAction = new EventEmitter<void>();

  @ViewChild('inputElement') private inputElement: ElementRef<HTMLInputElement>;

  /** For use without a form control; bound fields get theirs through `writeValue`. */
  @Input() value = '';
  private onChange = (value: string) => {
    // ControlValueAccessor callback
  };
  private onTouched = () => {
    // ControlValueAccessor callback
  };

  writeValue(value: string): void {
    // `??` not `||`: a numeric 0 is a real value and must render, not blank out.
    this.value = value ?? '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.value = target.value;
    this.onChange(this.value);
    this.inputChange.emit(this.value);
  }

  clear(): void {
    if (this.disabled) {
      return;
    }
    this.value = '';
    this.onChange('');
    this.inputChange.emit('');
    this.cleared.emit();
    this.inputElement?.nativeElement?.focus();
  }

  onBlur(): void {
    this.onTouched();
    this.focusoutAction.emit();
  }

  onFocus() {
    if (this.readonly) {
      this.inputElement?.nativeElement?.select();
    }
    this.focustEnterAction.emit();
  }

  selectAll(): void {
    const field = this.inputElement?.nativeElement;
    field?.focus();
    field?.select();
  }

  blur(): void {
    this.inputElement?.nativeElement?.blur();
  }
}
